import { getCookie } from '@jira/platform__browser-storage-controls/src/utils/get-cookie/index.tsx';
import { memoizedFetchSiteUserPersonalization } from '@jira/platform__services-personalization/src/services/site-user/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { InviteAppShortcutsCookieCache } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/cache';
import {
	useInvitedAppShortcuts as useInvitedAppShortcutsPlatform,
	type UseInvitedAppShortcutsResult,
} from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';

const targetProducts = ['confluence'] as const;

export const useResolveInvitedAppShortcuts = (): UseInvitedAppShortcutsResult => {
	const cloudId = useCloudId();
	const userId = useAccountId();

	return useInvitedAppShortcutsPlatform(
		fg('twcg-460-personalised-invite-notifications-m2-gate')
			? {
					enabled: true,
					sourceProduct: 'jira',
					targetProducts,
					userId,
					cloudId,
					cache: getJiraSsrCookieCacheInstance(),
					fetchTraits: memoizedFetchSiteUserPersonalization,
					fetchTraitsPolicy: 'prefer-cache',
				}
			: { enabled: false },
	);
};

let cache: InviteAppShortcutsCookieCache | undefined;

/**
 * Returns a process-singleton Jira-side cookie cache for Invited App Shortcuts.
 *
 * Exported so the productionisation cohort branch
 * (`GSNForContentSidebarWithInvitedAppRecommendations.tsx`) can pass the same
 * cache instance to `useInvitedAppShortcuts({ mode: 'productionisation' })`
 * as the legacy experiment branch above. Keeping the cookie cache warm in
 * productionisation matches the "Active in both modes — the cookie cache is
 * kept warm as a fallback against cohort flips / gate-off" contract in
 * `useInvitedAppShortcuts`.
 */
export const getJiraSsrCookieCacheInstance = (): InviteAppShortcutsCookieCache => {
	if (!cache) {
		cache = new InviteAppShortcutsCookieCache({ getCookie });
	}
	return cache;
};
